import { GetConnectionType, offlineClareStatus, onlineClareStatus, unconfiguredClareStatus } from "@app/shared/model/clare-status.model";
import { ProjectActions, ProjectFeatureActions, ProjectViewActions } from "../actions";
import { ProjectState, initialProjectState } from "../projects.state";

import { ProjectViewHelper } from "@app/project/model";
import { isProjectConnOnline } from "@app/shared/model/clare-status.model";
import { ROLE_NAMES } from "@app/user/model/user-role";
import { LoadingState } from "../../../root-store/api-result.state";
import { ActiveOrSuspendProjectSuccess } from "../actions/projects.actions";

const getUpdatedProjectAddress = (currentPayload, updatedPayload) => {
    if (!currentPayload?.address) {
        return updatedPayload?.address || null;
    }

    if (updatedPayload?.address) {
        return currentPayload?.address;
    }

    if (currentPayload?.addressId && currentPayload.addressId === updatedPayload?.addressId) {
        return updatedPayload.address || currentPayload.address;
    }

    return updatedPayload.address;
};

export const reducer = (state: ProjectState = initialProjectState, action: ProjectActions.Actions | any = null): ProjectState => {
    switch (action.type) {
        case ProjectActions.ActionTypes.CreateProject:
        case ProjectActions.ActionTypes.GetProjects:
        case ProjectActions.ActionTypes.GetProject:
        case ProjectActions.ActionTypes.UpdateProject: {
            return {
                ...state,
                projects: {
                    ...state.projects,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectActions.ActionTypes.DeleteProjectUserSuccess:
        case ProjectActions.ActionTypes.CreateProjectUserSuccess:
        case ProjectActions.ActionTypes.UpdateProjectSuccess: {
            const address = getUpdatedProjectAddress(state.activeProject.result, action.payload);
            return {
                ...state,
                activeProject: { result: { ...action.payload, address }, callState: LoadingState.loaded },
                activeProjectStatus: { result: null, callState: LoadingState.init },
            };
        }

        case ProjectActions.ActionTypes.UpdateProjectDealerSuccess: {
            return {
                ...state,
                activeProjectEntity: { result: null, callState: LoadingState.init },
            };
        }

        case ProjectActions.ActionTypes.CreateProjectSuccess: {
            // Because the project list view is dynamic, after adding a new project,
            // there is no point in adding it to the current state.
            // It will not likely contain all of the keys needed for the list view.
            return {
                ...state,
                projects: {
                    result: null,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.GetProjectsSuccess: {
            return {
                ...state,
                projects: {
                    result: action.payload.projects,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.ClearCentralStationProjectData: {
            return {
                ...state,
                centralStationProjects: {
                    result: null,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.GetCentralStationProjectDataSuccess: {
            return {
                ...state,
                centralStationProjects: {
                    result: action.payload.centralStationProjects,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.ClearProjects: {
            return {
                ...state,
                projects: {
                    result: [],
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectStatus: {
            return {
                ...state,
                activeProjectStatus: {
                    ...state.activeProjectStatus,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectStatusSuccess: {
            let status = offlineClareStatus;
            if (!action.result) {
                status = unconfiguredClareStatus;
            } else if (action.result) {
                const uuid = Object.keys(action.result)[0];
                if (isProjectConnOnline(action.result, uuid)) {
                    status = onlineClareStatus;
                }
            }
            return {
                ...state,
                activeProjectStatus: {
                    result: status,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetProjectSuccess: {
            return {
                ...state,
                projects: {
                    result: {
                        ...state.projects.result,
                        [action.payload.project.id]: action.payload.project,
                    },
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetProjectUsers: {
            return {
                ...state,
                users: {
                    ...state.users,
                    [action.payload]: { result: null, callState: LoadingState.loading },
                },
            };
        }

        case ProjectActions.ActionTypes.GetProjectUsersSuccess: {
            return {
                ...state,
                users: {
                    ...state.users,
                    [action.payload.projectId]: {
                        result: action.payload.users,
                        callState: LoadingState.loaded,
                    },
                },
            };
        }

        case ProjectActions.ActionTypes.CreateProjectFailure:
        case ProjectActions.ActionTypes.UpdateProjectFailure:
        case ProjectActions.ActionTypes.GetActiveProjectFailure:
        case ProjectActions.ActionTypes.GetProjectFailure:
        case ProjectActions.ActionTypes.ActiveOrSuspendProjectFailure:
        case ProjectActions.ActionTypes.GetProjectsFailure: {
            return {
                ...state,
                projects: {
                    ...state.projects,
                    callState: action.error,
                },
            };
        }

        case ProjectActions.ActionTypes.DeleteProjectSuccess: {
            const projects = { ...state.projects };
            const projectStatuses = { ...state.projectStatuses };
            delete projectStatuses.result[action.payload.id];
            projects.result = projects.result.filter(project => project.id !== action.payload.id);
            return {
                ...state,
                projects,
                projectStatuses,
            };
        }

        case ProjectActions.ActionTypes.ClearProjectState: {
            return {
                ...initialProjectState,
                activeProject: state.activeProject,
                selectedSubdealer: state.selectedSubdealer,
            };
        }

        case ProjectActions.ActionTypes.ClearProjectStatuses: {
            return {
                ...state,
                projectStatuses: {
                    ...state.projectStatuses,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.UpdateProjectStatuses: {
            const statuses = state.projectStatuses.result || {};
            const newStatuses = {};
            // set statuses for requested controllers to offline prior to result coming back
            // offline controllers will not be included in result
            if (action.payload) {
                // payload uuids is the online uuids from RALink
                // the state holds all projects and their statuses
                for (const project in statuses) {
                    if (statuses[project]) {
                        let status = null;
                        let connectionType = null;
                        if (!statuses[project].uuid) {
                            status = unconfiguredClareStatus;
                        }
                        newStatuses[project] = { ...statuses[project], status, connectionType };
                    }
                }
            }
            return {
                ...state,
                projectStatuses: {
                    ...state.projectStatuses,
                    result: newStatuses,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectActions.ActionTypes.UpdateProjectStatusesSuccess: {
            const statuses = state.projectStatuses.result || {};
            const uuids = !!action.result ? Object.keys(action.result) : [];
            const connectionTypes = action.result;
            const newStatuses = {};

            for (const status in statuses) {
                if (uuids.includes(statuses[status].uuid)) {
                    newStatuses[status] = Object.assign({}, statuses[status]);
                    newStatuses[status].status = onlineClareStatus;
                    const uuid = statuses[status].uuid;
                    newStatuses[status].connectionType = GetConnectionType(connectionTypes[uuid].connection);
                } else if (statuses[status].uuid) {
                    newStatuses[status] = Object.assign({}, statuses[status]);
                    newStatuses[status].status = offlineClareStatus;
                } else {
                    newStatuses[status] = Object.assign({}, statuses[status]);
                }
            }
            return {
                ...state,
                projectStatuses: {
                    result: newStatuses,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetProjectsUUIDsSuccess: {
            // Building up the entire store for state.projectStatuses, projectUuids, and projectStatusesByUuids
            const statuses = {};
            (action.payload || []).forEach((project: { uuid: string; project_id: number }) => {
                statuses[project.project_id] = project;
            });

            return {
                ...state,
                projectStatuses: {
                    result: statuses,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.ClearActiveProject: {
            return {
                ...state,
                activeProject: {
                    result: null,
                    callState: LoadingState.init,
                },
                activeProjectEntity: {
                    result: null,
                    callState: LoadingState.init,
                },
                activeProjectStatus: {
                    result: null,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.ClearActiveProjectStatus: {
            return {
                ...state,
                activeProjectStatus: {
                    result: null,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.ClearActiveProjectEntity: {
            return {
                ...state,
                activeProjectEntity: {
                    result: null,
                    callState: LoadingState.init,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProject: {
            return {
                ...state,
                activeProject: {
                    ...state.activeProject,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectSuccess: {
            return {
                ...state,
                activeProject: {
                    result: action.result,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectEntity: {
            return {
                ...state,
                activeProjectEntity: {
                    ...state.activeProjectEntity,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectActions.ActionTypes.SetSelectedEntitySuccess: {
            return {
                ...state,
                selectedSubdealer: {
                    result: action.result,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectEntitySuccess: {
            return {
                ...state,
                activeProjectEntity: {
                    result: action.result,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.GetActiveProjectEntityFailure: {
            return {
                ...state,
                activeProjectEntity: {
                    result: null,
                    callState: action.error,
                },
            };
        }

        case ProjectActions.ActionTypes.ActiveOrSuspendProjectSuccess: {
            const { projectId, isInactive } = action as ActiveOrSuspendProjectSuccess;
            const result = state.projects?.result || [];
            const changedIndex = result.findIndex(p => p.id === projectId);

            if (changedIndex >= 0) {
                result[changedIndex].inactive = isInactive;
            }
            return {
                ...state,
                projects: {
                    ...state.projects,
                    result: [...result],
                },
                activeProject: state.activeProject,
            };
        }

        case ProjectViewActions.loadSuccess.type: {
            return {
                ...state,
                projectViews: {
                    result: action.projectViews,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectViewActions.getSuccess.type: {
            return {
                ...state,
                projectView: {
                    result: action.projectView,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectViewActions.update.type:
        case ProjectViewActions.create.type: {
            return {
                ...state,
                projectView: { result: null, callState: LoadingState.loading },
            };
        }

        case ProjectViewActions.updateSuccess.type:
        case ProjectViewActions.createSuccess.type: {
            return {
                ...state,
                projectViews: {
                    ...state.projectViews,
                    result: {
                        ...state.projectViews.result,
                        [action.projectView?.id]: action.projectView,
                    },
                    callState: LoadingState.loaded,
                },
                projectView: {
                    ...state.projectView,
                    result: action.projectView,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectViewActions.deleteSuccess.type: {
            const authUser = Object.assign({}, action.authUser);
            const pmRoles = [ROLE_NAMES.PROPERTY_MANAGER_COORDINATOR, ROLE_NAMES.PROPERTY_MANAGER_ADMIN];
            const defaultView = pmRoles.includes(authUser.role.name)
                ? ProjectViewHelper.getPropertyManagerView(
                      authUser.entityId,
                      authUser.id,
                      authUser.role.name === ROLE_NAMES.PROPERTY_MANAGER_ADMIN
                  )
                : ProjectViewHelper.getDefaultProjectView(authUser.entityId, authUser.id);
            const projectViews = { ...state.projectViews.result };
            delete projectViews[action.projectView.id];
            return {
                ...state,
                projectViews: { result: projectViews, callState: LoadingState.loaded },
                projectView: {
                    result: defaultView,
                    callState: LoadingState.loaded,
                },
            };
        }

        case ProjectActions.ActionTypes.MoveOutSuccess: {
            return {
                ...state,
                activeProject: {
                    result: null,
                    callState: LoadingState.init,
                },
                activeProjectStatus: { result: null, callState: LoadingState.init },
            };
        }
        //========================================================
        // project feature
        //========================================================

        case ProjectFeatureActions.load.type: {
            return {
                ...state,
                activeProjectFeatures: {
                    ...state.activeProjectFeatures,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectFeatureActions.loadSuccess.type: {
            const result = action.payload;
            return {
                ...state,
                activeProjectFeatures: {
                    result,
                    callState: LoadingState.loaded,
                    error: null,
                },
            };
        }

        case ProjectFeatureActions.loadFailure.type: {
            console.log(action.error);
            return {
                ...state,
                activeProjectFeatures: {
                    ...state.activeProjectFeatures,
                    callState: LoadingState.loaded,
                    error: action.error,
                },
            };
        }

        //========================================================
        // project feature metadata
        //========================================================

        case ProjectFeatureActions.loadAllAdditionalFeatures.request.type: {
            return {
                ...state,
                allAdditionalFeatures: {
                    ...state.allAdditionalFeatures,
                    callState: LoadingState.loading,
                },
            };
        }

        case ProjectFeatureActions.loadAllAdditionalFeatures.success.type: {
            const result = action.payload;
            return {
                ...state,
                allAdditionalFeatures: {
                    result,
                    callState: LoadingState.loaded,
                    error: null,
                },
            };
        }

        case ProjectFeatureActions.loadAllAdditionalFeatures.failure.type: {
            console.log(action.error);
            return {
                ...state,
                allAdditionalFeatures: {
                    ...state.allAdditionalFeatures,
                    callState: LoadingState.loaded,
                    error: action.error,
                },
            };
        }
        case ProjectFeatureActions.clear.type: {
            return {
                ...state,
                activeProjectFeatures: {
                    result: null,
                    callState: LoadingState.init,
                    error: null,
                },
            };
        }
        case ProjectActions.ActionTypes.GetActivePanelSummary: {
            return {
                ...state,
                activePanelSummary: {
                    ...state.activePanelSummary,
                    callState: LoadingState.loading,
                },
            };
        }
        case ProjectActions.ActionTypes.GetActivePanelSummarySuccess: {
            const result = action.payload;
            return {
                ...state,
                activePanelSummary: {
                    ...state.activePanelSummary,
                    result,
                    callState: LoadingState.loaded,
                },
            };
        }
        case ProjectActions.ActionTypes.GetActivePanelSummaryFailure: {
            return {
                ...state,
                activePanelSummary: {
                    ...state.activePanelSummary,
                    result: null,
                    callState: LoadingState.loaded,
                    error: action.error,
                },
            };
        }
        case ProjectActions.ActionTypes.ClearActivePanelSummary: {
            return {
                ...state,
                activePanelSummary: {
                    result: null,
                    error: null,
                    callState: LoadingState.init,
                },
            };
        }
        default: {
            return state;
        }
    }
};
